import React, {useEffect, useState} from 'react';
import {observer} from 'mobx-react';
import {useIntl} from 'react-intl';
import {FormGroup, Input, Label} from 'reactstrap';
import useAlerts from 'src/hooks/use-alerts';
import ConfirmDialog from './confirm-dialog';
import EntitySearchField from './entity-search-field';
import {useEntityOptions} from './entities';
import {useCreateBlockExemption} from './mutations';
import {ExemptionRow} from './queries';
import {errorMessageId} from './errors';
import styles from './billing-block.module.scss';

type Kind = 'customer' | 'device';

interface ExemptionFormProps {
  open: boolean;
  exemptions: ExemptionRow[];
  onClose: () => void;
}

const ExemptionForm = ({open, exemptions, onClose}: ExemptionFormProps) => {
  const intl = useIntl();
  const {toast} = useAlerts();
  const create = useCreateBlockExemption();
  const {customers, devices} = useEntityOptions();
  const [kind, setKind] = useState<Kind>('customer');
  const [targetId, setTargetId] = useState<number | null>(null);
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!open) return;
    setKind('customer');
    setTargetId(null);
    setNote('');
  }, [open]);

  const exemptIds = (of: Kind) => exemptions.filter((row) => row.kind === of).map((row) => row.targetId);
  const ownerId = kind === 'device' ? devices.find((device) => device.id === targetId)?.customerId : undefined;
  const exemptOwner = ownerId && exemptIds('customer').includes(ownerId) ?
    customers.find((customer) => customer.id === ownerId) :
    undefined;

  const submit = () => {
    if (targetId === null) return;
    const trimmed = note.trim() || undefined;
    create.mutate(kind === 'customer' ? {customerId: targetId, note: trimmed} : {deviceId: targetId, note: trimmed}, {
      onSuccess: () => toast.success(intl.formatMessage({id: 'billingBlockExemptionCreated'})),
      onError: (error) => toast.error(intl.formatMessage({id: errorMessageId(error)})),
      onSettled: onClose,
    });
  };

  return (
    <ConfirmDialog
      open={open}
      title={intl.formatMessage({id: 'billingBlockNewExemption'})}
      description={<p className={styles.warning}>{intl.formatMessage({id: 'billingBlockExemptionWarning'})}</p>}
      confirmLabel={intl.formatMessage({id: 'billingBlockExempt'})}
      confirmDisabled={targetId === null}
      busy={create.isLoading}
      onConfirm={submit}
      onCancel={onClose}
    >
      <FormGroup className="custom-formGroup" role="radiogroup" aria-labelledby="billing-block-exemption-kind">
        <Label id="billing-block-exemption-kind" tag="div" className="font-weight-bold">{intl.formatMessage({id: 'billingBlockExemptionKind'})}</Label>
        {(['customer', 'device'] as const).map((option) => (
          <FormGroup check inline key={option}>
            <Input
              id={`billing-block-exemption-${option}`}
              type="radio"
              name="exemptionKind"
              value={option}
              checked={kind === option}
              onChange={() => {
                setKind(option);
                setTargetId(null);
              }}
            />
            <Label check className="font-weight-normal" for={`billing-block-exemption-${option}`}>
              {intl.formatMessage({id: option === 'customer' ? 'billingBlockKindCustomer' : 'billingBlockKindDevice'})}
            </Label>
          </FormGroup>
        ))}
      </FormGroup>
      <EntitySearchField
        key={kind}
        id="billing-block-exemption-target"
        kind={kind}
        label={intl.formatMessage({id: kind === 'customer' ? 'billingBlockKindCustomer' : 'billingBlockKindDevice'})}
        value={targetId}
        exclude={exemptIds(kind)}
        onChange={setTargetId}
      />
      {exemptOwner && (
        <p role="status" className={styles.hint}>
          {intl.formatMessage({id: 'billingBlockExemptionCustomerExempt'}, {customer: exemptOwner.label})}
        </p>
      )}
      <FormGroup className="custom-formGroup">
        <Label for="billing-block-exemption-note">{intl.formatMessage({id: 'billingBlockExemptionNote'})}</Label>
        <Input
          id="billing-block-exemption-note"
          type="textarea"
          rows={3}
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
      </FormGroup>
    </ConfirmDialog>
  );
};

export default observer(ExemptionForm);
